import React from "react";
import { Switch } from "@mui/material";
import { styled } from "@mui/material/styles";

// Bold "iOS-style" toggle Rahim referenced directly (2026-09-03) — solid
// filled pill track (no border/gap), a big thumb that nearly fills the
// track height, matching MUI's own documented iOS-switch customization
// recipe. Scoped to the AI Agents Config page only for now (Rahim: build
// it here first, decide separately whether to promote it into
// materialTheme.js's MuiSwitch override for the whole app later) — every
// plain `<Switch>` on this page (AgentConfigView.jsx's StepCreate/
// StepActions, OfficeHoursSection.jsx, ResponseFormatSection.jsx) swaps to
// this one shared component instead of each restyling its own instance,
// so the page doesn't end up with two different toggle designs mixed
// together. A standalone file (not a const inside AgentConfigView.jsx)
// specifically so OfficeHoursSection.jsx/ResponseFormatSection.jsx can
// import it without a circular import back through AgentConfigView.jsx,
// which imports them.
const PillSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 40, height: 24, padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0, margin: 2,
    transitionDuration: "200ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": { backgroundColor: theme.palette.primary.main, opacity: 1, border: 0 },
      "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.5 },
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.mode === "dark" ? theme.palette.grey[600] : theme.palette.grey[100],
    },
    "&.Mui-disabled + .MuiSwitch-track": { opacity: theme.palette.mode === "dark" ? 0.3 : 0.7 },
  },
  "& .MuiSwitch-thumb": { boxSizing: "border-box", width: 20, height: 20, boxShadow: "none" },
  "& .MuiSwitch-track": {
    borderRadius: 12,
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[300],
    opacity: 1,
    transition: theme.transitions.create(["background-color"], { duration: 200 }),
  },
}));

// Shared FormControlLabel sx for every PillSwitch label on this page
// (Rahim, 2026-09-03: "fix the text beside it as well ... weight correct
// and space between the text and the toggle") — PillSwitch's 40px box is
// tighter than the stock MUI Switch this page's label spacing was
// originally tuned against, so both the label's own font weight and the
// gap to the switch needed an explicit, deliberate value instead of
// whatever MUI's default FormControlLabel offset (built for a bigger
// switch) happened to produce. Targets `.MuiFormControlLabel-label`
// directly so `label` can stay a plain string everywhere instead of every
// call site wrapping its own Typography node.
export const pillSwitchLabelSx = {
  ml: 0,
  "& .MuiFormControlLabel-label": {
    ml: 1,
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "text.primary",
  },
};

// Same as above but for labelPlacement="start" (label sits to the LEFT of
// the switch, e.g. Office Hours' own "Enabled/Disabled" toggle) — the gap
// needs to go on the label's right side instead of its left.
export const pillSwitchLabelStartSx = {
  mr: 0,
  "& .MuiFormControlLabel-label": {
    mr: 1,
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "text.primary",
  },
};

export default PillSwitch;
